(function () {
  'use strict';

  var sbbNamespace = window.__SBB__ = window.__SBB__ || {};
  var ScaleBarBuilder = sbbNamespace.ScaleBarBuilder;
  var clamp = sbbNamespace.clamp;
  var parseNumber = sbbNamespace.parseNumber;
  var deepClone = sbbNamespace.deepClone;
  if (!ScaleBarBuilder || !clamp || !parseNumber || !deepClone) {
    return;
  }

  ScaleBarBuilder.prototype.getHistorySnapshot = function () {
    var snapshot = this.getSerializableState({ includeMedia: true });
    snapshot.lastSavedAt = null;
    snapshot.shareWarnings = [];
    return snapshot;
  };

  ScaleBarBuilder.prototype.initializeHistory = function () {
    var first = this.getHistorySnapshot();
    this.undoStack = [first];
    this.redoStack = [];
    this.lastHistorySerialized = JSON.stringify(first);
    this.updateHistoryButtons();
  };

  ScaleBarBuilder.prototype.updateHistoryButtons = function () {
    if (this.controls.undo) {
      this.controls.undo.disabled = this.undoStack.length <= 1;
    }
    if (this.controls.redo) {
      this.controls.redo.disabled = this.redoStack.length === 0;
    }
  };

  ScaleBarBuilder.prototype.commitHistory = function () {
    if (this.suspendHistory) {
      return;
    }
    var snapshot = this.getHistorySnapshot();
    var serialized = JSON.stringify(snapshot);
    if (serialized === this.lastHistorySerialized) {
      this.updateHistoryButtons();
      return;
    }
    this.undoStack.push(snapshot);
    if (this.undoStack.length > this.maxHistorySteps) {
      this.undoStack.shift();
    }
    this.redoStack = [];
    this.lastHistorySerialized = serialized;
    this.updateHistoryButtons();
  };

  ScaleBarBuilder.prototype.restoreStateFromHistorySnapshot = function (snapshot) {
    if (!snapshot) {
      return false;
    }
    this.suspendAutosave = true;
    this.suspendHistory = true;
    this.normalizeLoadedState(snapshot);
    this.refreshPresetDependentOptions(false);
    this.syncControlsFromState();
    this.syncDynamicRows();
    this.applySidebarCollapsedState();
    this.resizeCanvas();
    this.suspendHistory = false;
    this.suspendAutosave = false;
    this.render();
    this.lastHistorySerialized = JSON.stringify(this.getHistorySnapshot());
    return true;
  };

  ScaleBarBuilder.prototype.undo = function () {
    if (this.undoStack.length <= 1) {
      return;
    }
    var current = this.undoStack.pop();
    this.redoStack.push(current);
    var previous = this.undoStack[this.undoStack.length - 1];
    this.restoreStateFromHistorySnapshot(previous);
    this.updateHistoryButtons();
  };

  ScaleBarBuilder.prototype.redo = function () {
    if (this.redoStack.length === 0) {
      return;
    }
    var next = this.redoStack.pop();
    this.undoStack.push(deepClone(next));
    this.restoreStateFromHistorySnapshot(next);
    this.lastHistorySerialized = JSON.stringify(this.getHistorySnapshot());
    this.updateHistoryButtons();
  };

  ScaleBarBuilder.prototype.getCanvasCenterPointPx = function () {
    return {
      x: this.canvas.width / 2,
      y: this.canvas.height / 2
    };
  };

  ScaleBarBuilder.prototype.normalizeViewState = function () {
    var paper = this.getPaper();
    this.state.viewZoom = clamp(parseNumber(this.state.viewZoom, 1), 0.35, 8);
    var fallbackX = paper.width / 2;
    var fallbackY = paper.height / 2;
    var cx = parseNumber(this.state.viewCenterXmm, fallbackX);
    var cy = parseNumber(this.state.viewCenterYmm, fallbackY);
    this.state.viewCenterXmm = clamp(cx, -paper.width, paper.width * 2);
    this.state.viewCenterYmm = clamp(cy, -paper.height, paper.height * 2);
  };

  ScaleBarBuilder.prototype.setZoomLevel = function (nextZoom, anchorPx) {
    if (!this.view) {
      return;
    }
    var zoom = clamp(parseNumber(nextZoom, this.state.viewZoom), 0.35, 8);
    var anchor = anchorPx || this.getCanvasCenterPointPx();
    var mmAtAnchorX = (anchor.x - this.view.originX) / this.view.scale;
    var mmAtAnchorY = (anchor.y - this.view.originY) / this.view.scale;
    var newScale = this.view.fitScale * zoom;
    if (!isFinite(newScale) || newScale <= 0) {
      return;
    }
    var centerXmm = mmAtAnchorX - (anchor.x - this.canvas.width / 2) / newScale;
    var centerYmm = mmAtAnchorY - (anchor.y - this.canvas.height / 2) / newScale;
    this.state.viewZoom = zoom;
    this.state.viewCenterXmm = centerXmm;
    this.state.viewCenterYmm = centerYmm;
    this.render();
  };

  ScaleBarBuilder.prototype.adjustZoom = function (factor, anchorPx) {
    var next = this.state.viewZoom * parseNumber(factor, 1);
    this.setZoomLevel(next, anchorPx);
  };

  ScaleBarBuilder.prototype.resetZoom = function () {
    var paper = this.getPaper();
    this.state.viewZoom = 1;
    this.state.viewCenterXmm = paper.width / 2;
    this.state.viewCenterYmm = paper.height / 2;
    this.render();
  };

  ScaleBarBuilder.prototype.getPreviewZoomFactor = function () {
    if (this.isExportRender) {
      return 1;
    }
    return clamp(parseNumber(this.state.viewZoom, 1), 0.35, 8);
  };

  ScaleBarBuilder.prototype.scalePreviewPx = function (px) {
    return parseNumber(px, 0) * this.getPreviewZoomFactor();
  };

  ScaleBarBuilder.prototype.updateUnitLabels = function () {
    var imperial = this.state.unitSystem === 'imperial';
    this.unitLabels.tagSize.textContent = imperial ? 'Tag Size (in)' : 'Tag Size (mm)';
    this.unitLabels.spacing.textContent = imperial ? 'Center Spacing (in)' : 'Center Spacing (cm)';
    this.unitLabels.cornerMargin.textContent = imperial ? 'Corner Margin (in)' : 'Corner Margin (mm)';
    this.unitLabels.cutMargin.textContent = imperial ? 'Cut Margin (in)' : 'Cut Margin (cm)';
    this.unitLabels.printMargin.textContent = imperial ? 'Print Margin (in)' : 'Print Margin (mm)';
    this.unitLabels.snapGrid.textContent = imperial ? 'Snap Grid Size (in)' : 'Snap Grid Size (mm)';
    this.unitLabels.checkerWidth.textContent = imperial ? 'Checker Width (in)' : 'Checker Width (cm)';
    this.unitLabels.checkerHeight.textContent = imperial ? 'Checker Height (in)' : 'Checker Height (mm)';
    this.unitLabels.backgroundNoiseSize.textContent = imperial ? 'Noise Grain Size (in)' : 'Noise Grain Size (mm)';

    this.controls.tagSize.step = imperial ? '0.05' : '0.5';
    this.controls.spacing.step = imperial ? '0.05' : '0.1';
    this.controls.cornerMargin.step = imperial ? '0.05' : '0.5';
    this.controls.cutMargin.step = imperial ? '0.05' : '0.1';
    this.controls.printMargin.step = imperial ? '0.05' : '0.1';
    this.controls.snapGrid.step = imperial ? '0.25' : '0.1';
    this.controls.checkerWidth.step = imperial ? '0.05' : '0.1';
    this.controls.checkerHeight.step = imperial ? '0.05' : '0.1';
    this.controls.backgroundNoiseSize.step = imperial ? '0.05' : '0.1';
  };

  ScaleBarBuilder.prototype.applySidebarCollapsedState = function () {
    var collapsed = !!this.state.sidebarCollapsed;
    if (this.controls.layout) {
      this.controls.layout.classList.toggle('sbb-document-collapsed', collapsed);
    }
    if (this.controls.documentPanel) {
      this.controls.documentPanel.classList.toggle('is-collapsed', collapsed);
    }
    if (this.controls.advancedControls) {
      this.controls.advancedControls.classList.toggle('is-collapsed', collapsed);
    }
    if (this.controls.openAdvanced) {
      this.controls.openAdvanced.textContent = collapsed ? 'Show Settings' : 'Hide Settings';
      this.controls.openAdvanced.setAttribute('aria-pressed', collapsed ? 'false' : 'true');
    }
  };
})();
